import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { useMutation } from '@tanstack/react-query';
import { coinModes } from '@ui/hooks/useCoinMode';
import { useGardenDeposit } from '@ui/hooks/useGardenDeposit';
import {
  useVenoFountainHarvest,
  useVenoReservoirHarvest,
} from '@ui/hooks/useVenoHarvest';
import { gardenTokenDict, gardenTypes } from '@ui/utils/garden';
import type { VaultType } from '@ui/views/Vno/ReservoirAndFountain/types';
import type { EthersError } from '@veno-app/sdk';

import type { GardenWithCoinMode } from './useHarvestAllDrawer';
import {
  useHarvestAllDrawer,
  useHarvestAllRewardsInfo,
} from './useHarvestAllDrawer';

export const useHarvestAction = (cb?: () => void) => {
  const { mutateAsync: doGardenDeposit } = useGardenDeposit();
  const { addHarvestedGardenRewards } = useHarvestAllDrawer();
  const {
    defaultLockVault,
    addHarvestedGarden,
    addHarvestedVault,
    updateHarvestedFountainRewards,
    updateHarvestedReservoirRewards,
  } = useHarvestAllDrawer();
  const { pendingRewardsFountain, pendingRewardsMap, pendingRewardsReservoir } =
    useHarvestAllRewardsInfo();
  const { mutate: harvestFountain } = useVenoFountainHarvest({
    onSettled: () => {
      useHarvestAllDrawer.setState({
        isTransactionPending: false,
      });
    },
    onSuccess: () => {
      addHarvestedVault('FOUNTAIN');
      useHarvestAllDrawer.setState({
        harvestedFountainRewards: pendingRewardsFountain,
      });
      cb && cb();
    },
    onError: (err) => {
      useHarvestAllDrawer.setState({
        error: err as EthersError,
      });
    },
  });
  const { mutate: harvestReservoir } = useVenoReservoirHarvest({
    onSettled: () => {
      useHarvestAllDrawer.setState({
        isTransactionPending: false,
      });
    },
    onSuccess: () => {
      cb && cb();
      addHarvestedVault('RESERVOIR');
      useHarvestAllDrawer.setState({
        harvestedReservoirRewards: pendingRewardsReservoir,
      });
    },
    onError: (err) => {
      useHarvestAllDrawer.setState({
        error: err as EthersError,
      });
    },
  });

  return useMutation(
    async ({
      element,
    }: {
      element: VaultType | GardenWithCoinMode | undefined;
    }) => {
      useHarvestAllDrawer.setState({ isTransactionPending: true });
      if (isGardenWithCoinMode(element)) {
        try {
          await doGardenDeposit({
            gardenType: element[1],
            vaultType: defaultLockVault,
            _coinMode: element[0],
            amount: parseUnits(
              '0',
              gardenTokenDict[element[0]][element[1]].decimals,
            ),
          });
          if (defaultLockVault === 'RESERVOIR') {
            updateHarvestedReservoirRewards(
              pendingRewardsReservoir.wcro,
              pendingRewardsReservoir.atom,
            );
          } else {
            updateHarvestedFountainRewards(
              pendingRewardsFountain ?? BigNumber.from(0),
            );
          }
          addHarvestedVault(defaultLockVault);
          addHarvestedGarden(element);
          addHarvestedGardenRewards(
            element,
            pendingRewardsMap[element[0]][element[1]]?.data ??
              BigNumber.from(0),
            pendingRewardsMap[element[0]][element[1]]?.usd ?? BigNumber.from(0),
          );
          cb && cb();
        } catch (err) {
          useHarvestAllDrawer.setState({
            error: err as EthersError,
          });
        } finally {
          useHarvestAllDrawer.setState({ isTransactionPending: false });
        }
      } else if (element === 'FOUNTAIN') {
        harvestFountain();
      } else if (element === 'RESERVOIR') {
        harvestReservoir();
      }
    },
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isGardenWithCoinMode(obj: any): obj is GardenWithCoinMode {
  return (
    obj &&
    obj.length === 2 &&
    gardenTypes.includes(obj[1]) &&
    coinModes.includes(obj[0])
  );
}
